// src/crm/ugc/VideoThread.tsx
//
// Fil d'une vidéo dans l'onglet « Vidéos » de l'admin (plan de l'espace créateur v2, PR 2) : tout
// ce qui lui est arrivé, dans l'ordre (le même fil que le portail : idée, versions, relectures avec
// « ce qui marche » et « à retoucher », publications, captures et relevés, montant, messages), les
// messages de la personne pas encore lus marqués « Nouveau », puis la zone de réponse. Le message
// part au nom de l'équipe ; la personne le lit dans son espace, et un e-mail l'en prévient (au plus
// un par vidéo toutes les 30 minutes, dans le plafond du jour ; si elle a choisi un résumé par
// jour, dans son résumé du matin s'il n'est pas lu d'ici là : l'envoi dit sinon pourquoi). Le fil
// n'est marqué lu côté équipe que pour une vidéo choisie (`markRead` : clic, lien d'une alerte) ; un
// nouveau clic sur la vidéo affichée (`readRequest`) le relit en le marquant ; il se relit aussi
// toutes les 30 s tant que l'onglet est visible, sans rien marquer ni toucher à la réponse en cours. « Nouveau » se place
// d'après la lecture d'avant la première ouverture de la visite : un message arrivé pendant qu'on
// rédige reste « Nouveau » après la réponse. Sous notre dernier message, « Lu » ou « Pas encore lu »
// d'après la lecture de la personne (le portail, lui, ne montre jamais de « lu »).

import { useEffect, useId, useRef, useState, type FormEvent } from 'react'
import { UGC_RULES, formatEuros } from '../../../functions/lib/ugc/rules'
import { ApiClientError } from '../../lib/api'
import type { AdminVideoThread, ConversationEmailOutcome, ThreadItem, UgcPlatform } from '../../lib/ugcTypes'
import { formatDate, formatDateTime } from './format'
import { useSendTeamMessage, useUgcThread } from './hooks'

const PLATFORM_LABELS: Record<UgcPlatform, string> = { tiktok: 'TikTok', instagram: 'Instagram' }
const COUNT = new Intl.NumberFormat('fr-FR')

type ReviewItem = Extract<ThreadItem, { kind: 'review' }>
type PayoutItem = Extract<ThreadItem, { kind: 'payout' }>
type EventItem = Exclude<ThreadItem, { kind: 'message' }>

function quoted(comment: string | null): string {
  return comment ? ` : « ${comment} »` : ''
}

/** Une relecture, rattachée à sa version pour un brouillon : « Version 1 à corriger, le 11 nov. 2026 : « … » ». */
export function reviewText(review: ReviewItem): string {
  const date = formatDate(review.at)
  const version = review.draftVersion ?? undefined
  const subject = review.stage === 'idea' ? 'Idée' : version ? `Version ${version}` : 'Brouillon'
  const feminine = review.stage === 'idea' || version !== undefined
  switch (review.decision) {
    case 'approved':
      return `${subject} ${feminine ? 'validée' : 'validé'} le ${date}${quoted(review.comment)}`
    case 'changes_requested':
      return `${subject} ${review.stage === 'idea' ? 'à revoir' : 'à corriger'}, le ${date}${quoted(review.comment)}`
    case 'rejected':
      return `${subject} ${feminine ? 'refusée' : 'refusé'} le ${date}${quoted(review.comment)}`
  }
}

function payoutText(item: PayoutItem): string {
  const base = `Montant figé le ${formatDate(item.at)} : ${formatEuros(item.amountCents ?? 0)} pour ${COUNT.format(item.totalViews)} vues`
  switch (item.payState) {
    case 'none':
      return `${base}, ${item.overQuota ? 'au-delà du quota du mois : pas payé' : `sous le seuil minimal avant rémunération (${COUNT.format(UGC_RULES.thresholdViews)} vues) : non rémunéré`}`
    case 'due':
      return `${base}, à payer`
    case 'invoice_received':
      return `${base}, facture reçue`
    case 'paid':
      return `${base}, payé${item.paidAt ? ` le ${formatDate(item.paidAt)}` : ''}`
    case 'settled':
      return `${base}, réglé autrement`
  }
}

/** Texte d'un événement du fil (tout sauf les messages, affichés à part). */
function eventText(item: EventItem): string {
  switch (item.kind) {
    case 'idea':
      return `Idée envoyée le ${formatDate(item.at)}${item.revised ? ', corrigée depuis' : ''}`
    case 'review':
      return reviewText(item)
    case 'draft':
      return `Version ${item.version} reçue le ${formatDate(item.at)}${item.purged ? ' · fichier effacé' : ''}`
    case 'publication':
      return `Publiée sur ${PLATFORM_LABELS[item.platform]} le ${formatDate(item.publishedOn)} · relevé le ${formatDate(item.readingDueOn)}`
    case 'screenshot': {
      const views = item.declaredViews === null ? '' : ` : ${COUNT.format(item.declaredViews)} vues indiquées`
      return `Capture ${PLATFORM_LABELS[item.platform]} envoyée le ${formatDate(item.at)}${views}`
    }
    case 'screenshot_rejected':
      return `Capture ${PLATFORM_LABELS[item.platform]} refusée le ${formatDate(item.at)} : « ${item.comment} »`
    case 'reading':
      return `Relevé ${PLATFORM_LABELS[item.platform]} validé le ${formatDate(item.at)} : ${COUNT.format(item.views)} vues${item.source === 'manual' ? ', sans capture' : ''}`
    case 'payout':
      return payoutText(item)
  }
}

function ReviewPoints({ item }: { item: ReviewItem }) {
  if (item.highlights.length === 0 && item.fixes.length === 0) return null
  return (
    <span className="ugc-thread-points">
      {item.highlights.length > 0 && <span>{`Ce qui marche : ${item.highlights.join(' · ')}`}</span>}
      {item.fixes.length > 0 && <span>{`À retoucher : ${item.fixes.join(' · ')}`}</span>}
    </span>
  )
}

/**
 * Sous notre dernier message, admin seulement : « Lu » quand la personne a ouvert sa conversation
 * depuis (sa lecture, `memberReadAt`, atteint l'heure du message), « Pas encore lu » sinon. Pas
 * d'heure : la lecture enregistrée est celle du dernier message qu'elle a vu, pas l'heure de son
 * ouverture.
 */
export function ReadMark({ at, memberReadAt }: { at: string; memberReadAt: string | null }) {
  const read = memberReadAt !== null && memberReadAt >= at
  return <span className={read ? 'ugc-msg-read is-read' : 'ugc-msg-read'}>{read ? 'Lu' : 'Pas encore lu'}</span>
}

/** Clé du dernier message de l'équipe, sous lequel se place « Lu » ou « Pas encore lu ». */
export function lastTeamKey<T extends { author: 'member' | 'team' }>(items: readonly T[], keyOf: (item: T) => string): string | null {
  const last = [...items].reverse().find((item) => item.author === 'team')
  return last ? keyOf(last) : null
}

function ThreadList({ thread, readBefore }: { thread: AdminVideoThread; readBefore: string | null }) {
  const author = thread.memberName || 'La personne'
  const isNew = (item: Extract<ThreadItem, { kind: 'message' }>) =>
    item.author === 'member' && (readBefore === null || item.at > readBefore)
  const messages = thread.items.filter((item): item is Extract<ThreadItem, { kind: 'message' }> => item.kind === 'message')
  const readMarkKey = lastTeamKey(messages, (m) => m.key)
  return (
    <ol className="ugc-thread" aria-label="Fil de la vidéo">
      {thread.items.map((item) =>
        item.kind === 'message' ? (
          <li key={item.key} className={`ugc-msg ugc-msg--${item.author}`}>
            <span className="ugc-msg-meta">
              <span className="ugc-msg-author">{item.author === 'team' ? "L'équipe" : author}</span>
              <span>{formatDateTime(item.at)}</span>
              {isNew(item) && <span className="ugc-badge ugc-badge--warn">Nouveau</span>}
            </span>
            <span className="ugc-prose">{item.body}</span>
            {item.key === readMarkKey && <ReadMark at={item.at} memberReadAt={thread.memberReadAt ?? null} />}
          </li>
        ) : (
          <li key={item.key} className="ugc-thread-event">
            <span>{eventText(item)}</span>
            {item.kind === 'publication' && (
              <a className="ugc-link" href={item.url} target="_blank" rel="noopener noreferrer">
                Voir la publication
              </a>
            )}
            {item.kind === 'review' && <ReviewPoints item={item} />}
          </li>
        ),
      )}
    </ol>
  )
}

function sendErrorText(error: Error): string {
  if (!(error instanceof ApiClientError)) {
    return "Le message n'a pas pu partir (réseau indisponible). Réessaie : s'il était déjà parti, il est dans le fil."
  }
  if (error.status === 404) return "Cette vidéo n'existe plus : la personne l'a peut-être supprimée."
  if (error.code === 'network_error') return `Le message n'a pas été enregistré (erreur ${error.status}). Réessaie dans un instant.`
  return error.message
}

type Outcome = { ok: boolean; text: string }

const WINDOW = UGC_RULES.messageEmailWindowMinutes

/** Ce que l'envoi a fait de l'e-mail, et pourquoi aucun n'est parti le cas échéant (vidéo ou chat). */
export function sentText(email: ConversationEmailOutcome, memberName: string): string {
  const person = memberName || 'la personne'
  switch (email) {
    case 'sent':
      return `Message envoyé, avec un e-mail à ${person}.`
    case 'recent':
      return `Message envoyé, sans nouvel e-mail : un e-mail est parti il y a moins de ${WINDOW} minutes, ${person} verra ce message avec le précédent.`
    case 'daily_cap':
      return `Message envoyé, sans e-mail : le plafond de ${UGC_RULES.messageEmailsPerDay} e-mails de conversation par jour est atteint. ${memberName || 'La personne'} le lira dans son espace.`
    case 'failed':
      return "Message envoyé, mais l'e-mail n'est pas parti : la personne le lira dans son espace."
    case 'digest':
      return "Message envoyé. La personne a choisi un résumé par jour : ce message lui sera envoyé dans son résumé du matin s'il n'est pas lu d'ici là."
  }
}

/**
 * Réponse de l'équipe. Pas de maxLength sur le champ : il tronquerait un texte collé sans rien dire.
 * Le serveur applique le schéma des messages ; son refus (texte trop long) s'affiche et le texte
 * reste dans le champ, vidé seulement après un envoi réussi.
 */
function ReplyForm({ videoId, memberName }: { videoId: string; memberName: string }) {
  const send = useSendTeamMessage()
  // Brouillon et résultat vivent ici : ils restent quand le fil est relu après l'envoi.
  const [body, setBody] = useState('')
  const [outcome, setOutcome] = useState<Outcome | null>(null)
  const fieldId = useId()
  const helpId = useId()
  const empty = body.trim() === ''

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    if (empty || send.isPending) return
    setOutcome(null)
    send.mutate(
      { id: videoId, body: body.trim() },
      {
        onSuccess: ({ email }) => {
          setBody('')
          setOutcome({ ok: true, text: sentText(email, memberName) })
        },
        onError: (error) => setOutcome({ ok: false, text: sendErrorText(error) }),
      },
    )
  }

  return (
    <form className="ugc-inline-form" onSubmit={submit}>
      <label htmlFor={fieldId}>{memberName ? `Ton message à ${memberName}` : 'Ton message'}</label>
      <textarea
        id={fieldId}
        className="ugc-textarea"
        rows={3}
        value={body}
        aria-describedby={helpId}
        onChange={(e) => setBody(e.target.value)}
      />
      <p id={helpId} className="ugc-muted">
        {`La personne le lit dans son espace. Un e-mail la prévient, au plus un par vidéo toutes les ${WINDOW} minutes, ou son résumé du matin si elle l'a choisi. Écris au nom de l'équipe, en « nous ».`}
      </p>
      <div className="ugc-actions">
        <button type="submit" className="ugc-btn ugc-btn--strong" disabled={empty || send.isPending}>
          Envoyer le message
        </button>
      </div>
      {send.isPending && <p className="ugc-muted">Envoi du message…</p>}
      {outcome && (
        <p role={outcome.ok ? 'status' : 'alert'} className={outcome.ok ? 'ugc-notice' : 'ugc-error'}>
          {outcome.text}
        </p>
      )}
    </form>
  )
}

/** Fil chargé : la lecture d'avant la première ouverture de la visite place « Nouveau » jusqu'au bout. */
function LoadedThread({ thread, videoId }: { thread: AdminVideoThread; videoId: string }) {
  const [readBefore] = useState(thread.lastReadAt)
  return (
    <>
      <ThreadList thread={thread} readBefore={readBefore} />
      {thread.canWrite ? (
        <ReplyForm videoId={videoId} memberName={thread.memberName} />
      ) : (
        <p className="ugc-muted">Cette personne a quitté le programme : la conversation est fermée.</p>
      )}
    </>
  )
}

/**
 * Fil de la vidéo et zone de réponse ; conversation fermée quand la personne a quitté le programme.
 * `markRead` : la vidéo a été choisie, ses lectures la marquent lue ; `readRequest` change à chaque
 * nouveau clic sur elle, qui relit le fil en le marquant.
 */
export function VideoThread({
  videoId,
  markRead = false,
  readRequest = 0,
}: {
  videoId: string
  markRead?: boolean
  readRequest?: number
}) {
  const thread = useUgcThread(videoId, { markRead })
  // Relecture qui marque lu, gardée à jour à chaque rendu : seul un nouveau clic (`readRequest`) la lance.
  const refetchRead = useRef(thread.refetchRead)
  refetchRead.current = thread.refetchRead
  const headingId = useId()
  // Un clic après l'ouverture (vidéo déjà affichée) : le fil est relu, cette fois en le marquant.
  const seenRequest = useRef(readRequest)
  useEffect(() => {
    if (readRequest === seenRequest.current) return
    seenRequest.current = readRequest
    void refetchRead.current()
  }, [readRequest])
  return (
    <section className="ugc-history-block" aria-labelledby={headingId}>
      <h3 id={headingId} className="ugc-subtitle">
        Fil de la vidéo
      </h3>
      {thread.data ? (
        <LoadedThread thread={thread.data} videoId={videoId} />
      ) : thread.isError ? (
        <p className="ugc-error">{`Le fil n'a pas pu être chargé : ${thread.error.message}`}</p>
      ) : (
        <p className="ugc-muted">Chargement du fil…</p>
      )}
    </section>
  )
}
